"use client";

import {
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
  type RefObject,
} from "react";
import Link from "next/link";
import {
  calculateMatching,
  calculateDetailedMatching,
  downloadMatchingReport,
} from "@/lib/api/kundali";
import type { PersonInput, MatchingResponse, DetailedMatchingResponse } from "@/lib/api/kundali";
import type { KundaliRequest } from "@ishvaram/api-client";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import { createEventId } from "@/lib/beacon-types";
import { trackCalculatorUsed, trackMatchingCompleted } from "@/lib/analytics-free-tools";
import type { MatchingResultsPanelProps } from "@/components/matching/MatchingResultsPanel";
import { matchingCopy } from "@/components/matching/matching-copy";
import { inspectMatchingResult } from "@/lib/matching-result-quality";
import { jsErrorFieldsFromUnknown, reportJsError } from "@/lib/js-error-beacon";
import { useResultScrollAnchor } from "@/lib/use-result-scroll-anchor";
import { BELOW_STICKY_CHROME_SCROLL_MARGIN } from "@/lib/sticky-chrome-offsets";
import { emitValueMoment } from "@/lib/push-value-moment";
import { matchingCompletionContext } from "@/components/matching/MatchingSurface";

// Post-submission result rendering lives in the shared MatchingResultsPanel —
// the same component tree the /match/<id> share surface renders (rule 46).
const loadMatchingResultsPanel = () =>
  import("@/components/matching/MatchingResultsPanel").then((mod) => mod.MatchingResultsPanel);

function preloadMatchingResultsPanel() {
  void loadMatchingResultsPanel();
}

function assertCompleteMatchingResult(response: MatchingResponse, fallback: string): void {
  const quality = inspectMatchingResult(response);
  if (quality.complete) return;
  const reason = quality.reasons.join(",");
  reportJsError({
    error_name: "MatchingResultContractViolation",
    error_message: `matching result incomplete: ${reason}`,
    boundary: `KundliMatchingClient.result.${quality.reasons[0] ?? "unknown"}`,
  });
  throw new Error(fallback);
}

function reportMatchingCalculationFailure(error: unknown): void {
  reportJsError({
    ...jsErrorFieldsFromUnknown(error),
    boundary: "KundliMatchingClient.calculation_failed",
  });
}

function personInput(data: KundaliRequest): PersonInput {
  return {
    name: data.name,
    birth_date: data.birth_date,
    birth_time: data.birth_time,
    birth_place: data.birth_place,
    latitude: data.latitude ?? undefined,
    longitude: data.longitude ?? undefined,
    timezone: data.timezone ?? undefined,
  };
}

export default function KundliMatchingClient({
  defaultLanguage = "en",
}: {
  defaultLanguage?: "en" | "hi";
}) {
  const copy = matchingCopy(defaultLanguage);
  const [attemptId, setAttemptId] = useState(createEventId);
  const [personAData, setPersonAData] = useState<PersonInput | null>(null);
  const [personBData, setPersonBData] = useState<PersonInput | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MatchingResponse | null>(null);
  const [ResultsPanel, setResultsPanel] = useState<ComponentType<MatchingResultsPanelProps> | null>(
    null
  );
  const [detailedResult, setDetailedResult] = useState<DetailedMatchingResponse | null>(null);
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [detailedError, setDetailedError] = useState<string | null>(null);

  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Step tracking: 0 = enter A, 1 = enter B, 2 = results
  const step = result ? 2 : personAData ? 1 : 0;

  const resultRef = useMatchingResultMoment(result);

  async function handleSubmitA(data: KundaliRequest) {
    preloadMatchingResultsPanel();
    setPersonAData(personInput(data));
  }

  async function handleSubmitB(data: KundaliRequest) {
    if (!personAData) return;
    const resultsPanelReady = loadMatchingResultsPanel();

    const personB = personInput(data);
    setPersonBData(personB);
    setLoading(true);
    setError(null);

    try {
      const [response, ResultsPanelComponent] = await Promise.all([
        calculateMatching({
          person_a: personAData,
          person_b: personB,
          language: defaultLanguage,
        }),
        resultsPanelReady,
      ]);
      assertCompleteMatchingResult(response, copy.genericError);
      setResultsPanel(() => ResultsPanelComponent);
      setResult(response);
      trackBirthMatchingResult(response, attemptId, defaultLanguage);
    } catch (err) {
      reportMatchingCalculationFailure(err);
      setError(customerFacingError(err, defaultLanguage, copy.genericError));
    } finally {
      setLoading(false);
    }
  }

  async function handleGetDetailed() {
    if (!personAData || !personBData) return;
    setDetailedLoading(true);
    setDetailedError(null);

    try {
      const response = await calculateDetailedMatching({
        person_a: personAData,
        person_b: personBData,
        match_id: result?.match_id ?? null,
        language: defaultLanguage,
      });
      setDetailedResult(response);
      trackDetailedMatchingResult(result?.match_id, attemptId, defaultLanguage);
    } catch (err) {
      setDetailedError(customerFacingError(err, defaultLanguage, copy.detailedError));
    } finally {
      setDetailedLoading(false);
    }
  }

  async function handleDownloadPdf() {
    if (!personAData || !personBData) return;
    setPdfLoading(true);
    setPdfError(null);
    try {
      const blob = await downloadMatchingReport({
        person_a: personAData,
        person_b: personBData,
        language: defaultLanguage,
      });
      saveMatchingPdf(blob, `Matching_Report_${personAData.name}_${personBData.name}.pdf`);
    } catch {
      setPdfError(copy.pdfError);
      reportJsError({
        error_name: "MatchingPdfDownloadFailed",
        error_message: "Matching PDF download could not be started",
        boundary: "KundliMatchingClient.pdf_download_failed",
      });
    } finally {
      setPdfLoading(false);
    }
  }

  function handleReset() {
    setPersonAData(null);
    setPersonBData(null);
    setResult(null);
    setAttemptId(createEventId());
    setDetailedResult(null);
    setDetailedError(null);
    setPdfError(null);
    setError(null);
  }

  return (
    <>
      {!result && <MatchByNameLink language={defaultLanguage} />}
      <MatchingFormSteps
        attemptId={attemptId}
        step={step}
        personAData={personAData}
        loading={loading}
        error={error}
        defaultLanguage={defaultLanguage}
        onSubmitA={handleSubmitA}
        onSubmitB={handleSubmitB}
        onChangePersonA={() => {
          setPersonAData(null);
          setPersonBData(null);
        }}
      />

      {loading && <MatchingLoading language={defaultLanguage} />}
      <MatchingSubmitError error={error} step={step} loading={loading} />
      {result && ResultsPanel && (
        <MatchingResultAnchor anchorRef={resultRef} language={defaultLanguage}>
          <ResultsPanel
            surface="tool"
            result={result}
            personAData={personAData}
            personBData={personBData}
            defaultLanguage={defaultLanguage}
            detailedResult={detailedResult}
            detailedLoading={detailedLoading}
            detailedError={detailedError}
            pdfLoading={pdfLoading}
            pdfError={pdfError}
            onGetDetailed={handleGetDetailed}
            onDownloadPdf={handleDownloadPdf}
            onReset={handleReset}
          />
        </MatchingResultAnchor>
      )}
    </>
  );
}

/**
 * The two things a Gun Milan result does as it lands:
 *  - the score itself comes into view (under the 100px header) and takes focus;
 *    top:0 used to put a phone back on the page hero with the score below the
 *    fold (C12);
 *  - it announces the value moment. Both locales mount the push slide-in with
 *    trigger="value_moment", which waits for this signal instead of
 *    interrupting the form; without the emit that card would silently never
 *    show (C03). Post-paint, once per result.
 */
function useMatchingResultMoment(result: MatchingResponse | null) {
  const resultRef = useRef<HTMLDivElement | null>(null);
  useResultScrollAnchor(Boolean(result), resultRef);
  useEffect(() => {
    if (result) emitValueMoment("matching_result");
  }, [result]);
  return resultRef;
}

function MatchingResultAnchor({
  anchorRef,
  language,
  children,
}: {
  anchorRef: RefObject<HTMLDivElement | null>;
  language: "en" | "hi";
  children: ReactNode;
}) {
  return (
    <div
      ref={anchorRef}
      tabIndex={-1}
      role="region"
      aria-label={language === "hi" ? "गुण मिलान का परिणाम" : "Gun Milan result"}
      data-matching-result-anchor
      className={`outline-none ${BELOW_STICKY_CHROME_SCROLL_MARGIN}`}
    >
      {children}
    </div>
  );
}

/**
 * Name-based matching lives on /gun-milan/ (and /hi/gun-milan/), the one owner
 * of "gun milan by name" per language — the intent was split across four URLs
 * before the 2026-07 drop (SEO audit 2026-09-28, w3-matching). This page keeps
 * the birth-details match and hands a seeker without a birth time one link.
 * @internal
 */
export const MATCH_BY_NAME_LINK = {
  en: { href: "/gun-milan/", label: "No birth time? Match by name on the Gun Milan page →" },
  hi: { href: "/hi/gun-milan/", label: "जन्म समय नहीं पता? नाम से गुण मिलान करें →" },
} as const;

function MatchByNameLink({ language }: { language: "en" | "hi" }) {
  const link = MATCH_BY_NAME_LINK[language];
  return (
    <p className="mx-auto mb-4 max-w-md text-center text-body-small">
      <Link href={link.href} className="font-semibold text-intent-brand underline">
        {link.label}
      </Link>
    </p>
  );
}

function saveMatchingPdf(blob: Blob, filename: string) {
  const downloadUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  try {
    anchor.href = downloadUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
  } finally {
    anchor.remove();
    URL.revokeObjectURL(downloadUrl);
  }
}

function trackBirthMatchingResult(
  response: MatchingResponse,
  attemptId: string,
  language: "en" | "hi"
) {
  const completion = {
    ...matchingCompletionContext("tool", language, "birth_details"),
    attempt_id: attemptId,
    match_id: response.match_id,
  };
  trackCalculatorUsed({ calculator: "kundli-matching", ...completion });
  trackMatchingCompleted({ score: response.total_score ?? 0, ...completion });
}

function trackDetailedMatchingResult(
  matchId: MatchingResponse["match_id"],
  attemptId: string,
  language: "en" | "hi"
) {
  trackCalculatorUsed({
    calculator: "kundli-matching-detailed",
    attempt_id: attemptId,
    ...matchingCompletionContext("tool", language, "birth_details"),
    match_id: matchId,
  });
}

function customerFacingError(error: unknown, language: "en" | "hi", fallback: string): string {
  if (language === "hi") return fallback;
  return error instanceof Error ? error.message : fallback;
}

function MatchingFormSteps({
  attemptId,
  step,
  personAData,
  loading,
  error,
  defaultLanguage,
  onSubmitA,
  onSubmitB,
  onChangePersonA,
}: {
  attemptId: string;
  step: number;
  personAData: PersonInput | null;
  loading: boolean;
  error: string | null;
  defaultLanguage: "en" | "hi";
  onSubmitA: (data: KundaliRequest) => void;
  onSubmitB: (data: KundaliRequest) => void;
  onChangePersonA: () => void;
}) {
  if (step === 0) {
    return (
      <div className="max-w-md mx-auto">
        <BirthDetailsForm
          onSubmit={onSubmitA}
          loading={false}
          submitLabel={matchingCopy(defaultLanguage).nextPersonB}
          heading={matchingCopy(defaultLanguage).personA}
          headingLevel="h2"
          optionalName
          requireBirthTime
          language={defaultLanguage}
          analyticsToolName="kundli-matching"
          analyticsAttemptId={attemptId}
          analyticsStep="person_a"
          showTrustStrip
        />
      </div>
    );
  }
  if (step !== 1 || !personAData) return null;
  return (
    <div className="space-y-4">
      <PersonSummary
        personAData={personAData}
        onChange={onChangePersonA}
        language={defaultLanguage}
      />
      <BirthDetailsForm
        onSubmit={onSubmitB}
        loading={loading}
        submitLabel={matchingCopy(defaultLanguage).calculate}
        error={error ?? undefined}
        heading={matchingCopy(defaultLanguage).personB}
        headingLevel="h2"
        optionalName
        requireBirthTime
        language={defaultLanguage}
        analyticsToolName="kundli-matching"
        analyticsAttemptId={attemptId}
        analyticsStep="person_b"
      />
    </div>
  );
}

function PersonSummary({
  personAData,
  onChange,
  language,
}: {
  personAData: PersonInput;
  onChange: () => void;
  language: "en" | "hi";
}) {
  const copy = matchingCopy(language);
  return (
    <div className="card-sacred p-4 flex items-center justify-between">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-ish-text-faint">
          {copy.personA}
        </p>
        <p className="text-body font-bold text-ish-dark">{personAData.name}</p>
        <p className="text-caption text-ish-text-muted">
          {personAData.birth_date} &middot; {personAData.birth_place}
        </p>
      </div>
      <button onClick={onChange} className="text-caption font-semibold text-intent-brand">
        {copy.change}
      </button>
    </div>
  );
}

function MatchingLoading({ language }: { language: "en" | "hi" }) {
  const copy = matchingCopy(language);
  return (
    <div className="text-center py-12">
      <div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin mx-auto border-ish-accent" />
      <p className="mt-4 text-body text-ish-text-muted">{copy.loading}</p>
      <p className="mt-1 text-caption text-ish-text-faint">{copy.loadingDetail}</p>
    </div>
  );
}

function MatchingSubmitError({
  error,
  step,
  loading,
}: {
  error: string | null;
  step: number;
  loading: boolean;
}) {
  if (!error || step !== 1 || loading) return null;
  return (
    <div className="mt-4 text-body-small rounded-lg p-3.5 text-center text-error bg-error/5 border border-error/20">
      {error}
    </div>
  );
}
